import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  getTeamMembers,
  getTeamRoster,
  getTruppPreferences,
  updateTruppPreferences,
  resetTruppPreferences
} from '../lib/api';
import { toast } from 'sonner';

// System defaults
export const SYSTEM_DEFAULTS = {
  playerSort: 'position_asc',
  leaderSort: 'role',
  positionFilter: 'all'
};

// Position order for sorting
const POSITION_ORDER = {
  'Målvakt': 1,
  'Back': 2,
  'Mittfält': 3,
  'Anfall': 4
};

// Canonical team roles that are staff/leader identities in memberships.
const LEADER_ROLES = new Set([
  'team_admin',
  'head_coach',
  'coach',
  'assistant_coach',
  'goalkeeper_coach',
  'fitness_coach',
  'analyst',
  'team_manager',
  'leader',
  'staff',
  'admin'
]);

// Role order for sorting leaders
const ROLE_ORDER = {
  'head_coach': 1,
  'Huvudledare': 1,
  'assistant_coach': 2,
  'Assisterande ledare': 2,
  'goalkeeper_coach': 3,
  'Målvaktstränare': 3,
  'fitness_coach': 4,
  'Fystränare': 4,
  'analyst': 5,
  'Analytiker': 5,
  'team_manager': 6,
  'team_admin': 7,
  'coach': 8,
  'leader': 8,
  'staff': 8,
  'admin': 8
};

const extractRosterRows = (response) => {
  const payload = response?.data;

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.players)) return payload.players;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.roster)) return payload.roster;

  return [];
};

const normalizePlayerProfile = (profile, linkedAccount = null) => {
  const profileId = profile?.id || profile?._id;
  if (!profileId) return null;

  const linkedUserId = profile?.linked_user_id || profile?.user_id || null;
  const displayName = (
    profile?.display_name ||
    profile?.name ||
    [profile?.first_name, profile?.last_name].filter(Boolean).join(' ')
  ).trim();

  return {
    ...profile,
    id: String(profileId),
    player_profile_id: String(profileId),
    role: 'player',
    name: displayName || 'Okänd',
    linked_user_id: linkedUserId ? String(linkedUserId) : null,
    user_id: linkedUserId ? String(linkedUserId) : null,
    avatar_url: profile?.avatar_url || linkedAccount?.avatar_url || null,
    email: profile?.child_email || profile?.email || linkedAccount?.email || null,
    phone: profile?.phone || linkedAccount?.phone || null,
  };
};

/**
 * Custom hook for managing the Trupp view.
 *
 * Identity boundary:
 * - players come ONLY from player_profiles via getTeamRoster()
 * - leaders come ONLY from exact team memberships via getTeamMembers()
 * - linked user accounts may enrich a player with avatar/contact data, but never
 *   create or replace a player row
 */
export function useTeamMembers(teamId, userId) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preferences, setPreferences] = useState(SYSTEM_DEFAULTS);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Load canonical players + exact-team leaders.
  const loadMembers = useCallback(async () => {
    if (!teamId) {
      setMembers([]);
      return;
    }

    const [rosterResult, membersResult] = await Promise.allSettled([
      getTeamRoster(teamId),
      getTeamMembers(teamId)
    ]);

    const accountMembers = membersResult.status === 'fulfilled' && Array.isArray(membersResult.value?.data)
      ? membersResult.value.data
      : [];

    if (membersResult.status === 'rejected') {
      console.error('Error loading team memberships:', membersResult.reason);
    }

    const linkedAccountsById = new Map(
      accountMembers
        .filter((member) => member?.id)
        .map((member) => [String(member.id), member])
    );

    let canonicalPlayers = [];
    if (rosterResult.status === 'fulfilled') {
      canonicalPlayers = extractRosterRows(rosterResult.value)
        .map((profile) => {
          const linkedUserId = profile?.linked_user_id || profile?.user_id;
          const linkedAccount = linkedUserId
            ? linkedAccountsById.get(String(linkedUserId)) || null
            : null;

          return normalizePlayerProfile(profile, linkedAccount);
        })
        .filter(Boolean);
    } else {
      console.error('Error loading canonical roster:', rosterResult.reason);
      toast.error('Kunde inte ladda spelartruppen');
    }

    const canonicalLeaders = accountMembers.filter((member) =>
      LEADER_ROLES.has(member?.role)
    );

    // Combined array is kept for existing Team.jsx consumers, but player rows are
    // now exclusively canonical player_profiles. Membership-only players are not
    // synthesized into the Trupp view.
    setMembers([
      ...canonicalPlayers,
      ...canonicalLeaders
    ]);
  }, [teamId]);

  // Load preferences
  const loadPreferences = useCallback(async () => {
    if (!teamId || !userId) return;

    try {
      const res = await getTruppPreferences(userId, teamId);
      const prefs = res.data || res;

      setPreferences({
        playerSort: prefs.player_sort || SYSTEM_DEFAULTS.playerSort,
        leaderSort: prefs.leader_sort || SYSTEM_DEFAULTS.leaderSort,
        positionFilter: prefs.position_filter || SYSTEM_DEFAULTS.positionFilter
      });
    } catch (error) {
      console.error('Error loading preferences:', error);
      setPreferences(SYSTEM_DEFAULTS);
    }
  }, [teamId, userId]);

  // Initial load
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);

      await Promise.all([
        loadMembers(),
        loadPreferences()
      ]);

      setLoading(false);
    };

    loadAll();
  }, [loadMembers, loadPreferences]);

  // Update preference
  const updatePreference = useCallback((key, value) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: value
    }));

    setHasUnsavedChanges(true);
  }, []);

  // Save preferences
  const savePreferences = useCallback(async () => {
    if (!teamId || !userId) return;

    try {
      await updateTruppPreferences(userId, teamId, {
        player_sort: preferences.playerSort,
        leader_sort: preferences.leaderSort,
        position_filter: preferences.positionFilter,
        save_as_default: true
      });

      setHasUnsavedChanges(false);
      toast.success('Vyinställningar sparade');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Kunde inte spara inställningar');
    }
  }, [teamId, userId, preferences]);

  // Reset preferences
  const resetPreferencesToDefault = useCallback(async () => {
    if (!teamId || !userId) return;

    try {
      await resetTruppPreferences(userId, teamId);

      setPreferences(SYSTEM_DEFAULTS);
      setHasUnsavedChanges(false);

      toast.success('Standardinställningar återställda');
    } catch (error) {
      console.error('Error resetting preferences:', error);
      toast.error('Kunde inte återställa inställningar');
    }
  }, [teamId, userId]);

  // Get role display name for a leader
  const getRoleDisplayName = useCallback((member) => {
    if (member.display_role) return member.display_role;

    switch (member.role) {
      case 'head_coach':
        return 'Huvudledare';

      case 'assistant_coach':
        return 'Assisterande ledare';

      case 'goalkeeper_coach':
        return 'Målvaktstränare';

      case 'fitness_coach':
        return 'Fystränare';

      case 'analyst':
        return 'Analytiker';

      case 'team_manager':
        return 'Lagledare';

      case 'team_admin':
        return 'Lagadmin';

      default:
        return 'Ledare';
    }
  }, []);

  // Get role order for sorting
  const getRoleOrder = useCallback((member) => {
    const displayRole = getRoleDisplayName(member);

    return (
      ROLE_ORDER[displayRole] ||
      ROLE_ORDER[member.role] ||
      9
    );
  }, [getRoleDisplayName]);

  // Sort players
  const sortPlayers = useCallback((players, sortKey) => {
    return [...players].sort((a, b) => {
      switch (sortKey) {
        case 'position_asc': {
          const posA = POSITION_ORDER[a.position] || 99;
          const posB = POSITION_ORDER[b.position] || 99;

          if (posA !== posB) {
            return posA - posB;
          }

          return (
            (a.jersey_number || 99) -
            (b.jersey_number || 99)
          );
        }

        case 'position_desc': {
          const posA = POSITION_ORDER[a.position] || 0;
          const posB = POSITION_ORDER[b.position] || 0;

          if (posA !== posB) {
            return posB - posA;
          }

          return (
            (b.jersey_number || 0) -
            (a.jersey_number || 0)
          );
        }

        case 'jersey_asc':
          return (
            (a.jersey_number || 99) -
            (b.jersey_number || 99)
          );

        case 'jersey_desc':
          return (
            (b.jersey_number || 0) -
            (a.jersey_number || 0)
          );

        case 'name_asc':
          return (a.name || '').localeCompare(
            b.name || '',
            'sv'
          );

        case 'name_desc':
          return (b.name || '').localeCompare(
            a.name || '',
            'sv'
          );

        default:
          return 0;
      }
    });
  }, []);

  // Sort leaders
  const sortLeaders = useCallback((leaders, sortKey) => {
    return [...leaders].sort((a, b) => {
      switch (sortKey) {
        case 'role': {
          const roleOrderA = getRoleOrder(a);
          const roleOrderB = getRoleOrder(b);

          if (roleOrderA !== roleOrderB) {
            return roleOrderA - roleOrderB;
          }

          return (a.name || '').localeCompare(
            b.name || '',
            'sv'
          );
        }

        case 'name_asc':
          return (a.name || '').localeCompare(
            b.name || '',
            'sv'
          );

        case 'name_desc':
          return (b.name || '').localeCompare(
            a.name || '',
            'sv'
          );

        default:
          return 0;
      }
    });
  }, [getRoleOrder]);

  // Filter and sort the already-canonical combined member list.
  const { players, leaders } = useMemo(() => {
    if (!Array.isArray(members) || members.length === 0) {
      return {
        players: [],
        leaders: []
      };
    }

    const allPlayers = members.filter(
      (member) => member.role === 'player'
    );

    const allLeaders = members.filter(
      (member) => LEADER_ROLES.has(member.role)
    );

    let filteredPlayers = allPlayers;

    if (preferences.positionFilter !== 'all') {
      const filterPositions = {
        goalkeeper: ['Målvakt'],
        defender: ['Back'],
        midfielder: ['Mittfält'],
        forward: ['Anfall']
      };

      const allowedPositions =
        filterPositions[preferences.positionFilter] || [];

      filteredPlayers = allPlayers.filter(
        (player) =>
          allowedPositions.includes(player.position)
      );
    }

    return {
      players: sortPlayers(
        filteredPlayers,
        preferences.playerSort
      ),
      leaders: sortLeaders(
        allLeaders,
        preferences.leaderSort
      )
    };
  }, [
    members,
    preferences,
    sortPlayers,
    sortLeaders
  ]);

  return {
    members,
    players,
    leaders,
    loading,
    preferences,
    hasUnsavedChanges,
    updatePreference,
    savePreferences,
    resetPreferencesToDefault,
    getRoleDisplayName,
    refreshMembers: loadMembers
  };
}